// GPX course parsing and geometry for the GPX view of the event control map.
// Zero dependencies on purpose — GPX is simple XML and the repo carries no
// geo/XML libraries. Courses are parsed in the browser at upload time and
// only the simplified [lng, lat] polyline is sent to the server.

// Must stay in sync with MAX_GPX_POINTS in
// server/src/routers/commandControl/eventControl/gpxRoutes.js.
export const MAX_GPX_POINTS = 2000
export const MAX_GPX_ROUTES = 10

// Line colours for courses — high-contrast against the Carto Voyager
// basemap, same family as the map-pin palette (MapPins.jsx).
export const GPX_ROUTE_COLORS = [
  '#DC2626',
  '#2563EB',
  '#16A34A',
  '#7C3AED',
  '#EA580C',
  '#0D9488',
  '#DB2777',
  '#CA8A04',
  '#475569',
  '#92400E',
]

const toRadians = (degrees) => (degrees * Math.PI) / 180

// Haversine, same recipe as patientTransport/proximity.js (not imported —
// that module carries PTS journey logic this page tree shouldn't pull in).
export const distanceMetres = ([fromLng, fromLat], [toLng, toLat]) => {
  const earthRadiusMetres = 6371000
  const latDelta = toRadians(toLat - fromLat)
  const lngDelta = toRadians(toLng - fromLng)
  const a =
    Math.sin(latDelta / 2) * Math.sin(latDelta / 2) +
    Math.cos(toRadians(fromLat)) *
      Math.cos(toRadians(toLat)) *
      Math.sin(lngDelta / 2) *
      Math.sin(lngDelta / 2)
  return earthRadiusMetres * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export const routeDistanceMeters = (coordinates = []) =>
  coordinates.reduce(
    (total, point, index) =>
      index ? total + distanceMetres(coordinates[index - 1], point) : 0,
    0,
  )

const pointsFrom = (doc, selector) =>
  Array.from(doc.querySelectorAll(selector))
    .map((node) => [Number(node.getAttribute('lon')), Number(node.getAttribute('lat'))])
    .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))

const dropConsecutiveDuplicates = (coordinates) =>
  coordinates.filter(
    (point, index) =>
      !index ||
      point[0] !== coordinates[index - 1][0] ||
      point[1] !== coordinates[index - 1][1],
  )

// Parse a GPX document into { name, coordinates } or null when it holds no
// usable course. Multiple <trk>/<trkseg> are concatenated into one line —
// v1 treats a file as one course; separate courses are separate uploads.
export const parseGpx = (xmlText) => {
  let doc
  try {
    doc = new DOMParser().parseFromString(xmlText, 'application/xml')
  } catch (e) {
    return null
  }
  if (!doc || doc.querySelector('parsererror')) return null

  // Tracks first (recorded/planned courses), then routes, then bare
  // waypoints as a last resort.
  let coordinates = pointsFrom(doc, 'trk trkseg trkpt')
  if (coordinates.length < 2) coordinates = pointsFrom(doc, 'rte rtept')
  if (coordinates.length < 2) coordinates = pointsFrom(doc, 'wpt')

  coordinates = dropConsecutiveDuplicates(coordinates)
  if (coordinates.length < 2) return null

  const name =
    doc.querySelector('trk > name')?.textContent?.trim() ||
    doc.querySelector('metadata > name')?.textContent?.trim() ||
    null

  return { name, coordinates }
}

// Perpendicular distance (in degrees — fine for simplification tolerance)
// from a point to the segment start-end.
const perpendicularDistanceDeg = (point, start, end) => {
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  if (dx === 0 && dy === 0) return Math.hypot(point[0] - start[0], point[1] - start[1])
  const t = Math.max(
    0,
    Math.min(
      1,
      ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / (dx * dx + dy * dy),
    ),
  )
  return Math.hypot(point[0] - (start[0] + t * dx), point[1] - (start[1] + t * dy))
}

// Stack-based Douglas-Peucker (GPS tracks run to tens of thousands of
// points — recursion would be a stack-overflow risk), then a uniform
// downsample if the result is still over the point cap.
export const simplifyRoute = (
  coordinates,
  { toleranceDeg = 0.00005, maxPoints = MAX_GPX_POINTS } = {},
) => {
  if (!Array.isArray(coordinates) || coordinates.length <= 2) {
    return coordinates ?? []
  }

  const keep = new Array(coordinates.length).fill(false)
  keep[0] = true
  keep[coordinates.length - 1] = true

  const stack = [[0, coordinates.length - 1]]
  while (stack.length) {
    const [first, last] = stack.pop()
    let maxDistance = 0
    let index = -1
    for (let i = first + 1; i < last; i += 1) {
      const distance = perpendicularDistanceDeg(
        coordinates[i],
        coordinates[first],
        coordinates[last],
      )
      if (distance > maxDistance) {
        maxDistance = distance
        index = i
      }
    }
    if (index !== -1 && maxDistance > toleranceDeg) {
      keep[index] = true
      stack.push([first, index], [index, last])
    }
  }

  let simplified = coordinates.filter((_, index) => keep[index])

  if (simplified.length > maxPoints) {
    const step = (simplified.length - 1) / (maxPoints - 1)
    const sampled = []
    for (let i = 0; i < maxPoints; i += 1) {
      sampled.push(simplified[Math.round(i * step)])
    }
    sampled[sampled.length - 1] = simplified[simplified.length - 1]
    simplified = sampled
  }

  return simplified
}

// Interpolated [lng, lat] at every whole kilometre along the course —
// [{ km: 1, lngLat }, ...]. A single long segment can cross several
// kilometre boundaries, hence the inner loop.
export const kilometreMarkers = (coordinates = []) => {
  const markers = []
  let travelled = 0
  let nextKm = 1

  for (let i = 1; i < coordinates.length; i += 1) {
    const from = coordinates[i - 1]
    const to = coordinates[i]
    const segment = distanceMetres(from, to)
    if (!segment) continue

    while (travelled + segment >= nextKm * 1000) {
      const t = (nextKm * 1000 - travelled) / segment
      markers.push({
        km: nextKm,
        lngLat: [
          from[0] + (to[0] - from[0]) * t,
          from[1] + (to[1] - from[1]) * t,
        ],
      })
      nextKm += 1
    }
    travelled += segment
  }

  return markers
}

export const formatRouteDistance = (metres) => {
  if (!Number.isFinite(metres)) return ''
  return `${(metres / 1000).toFixed(1)} km`
}
